import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import {
    ArrowLeft, Loader2, ClipboardCheck, Users, Plus, Calendar, User, Phone, Mail,
    Euro, Trash2, Eye, CheckCircle2, X, AlertCircle, Truck, Package, MapPin, Search,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { usePermissions } from '../contexts/PermissionsContext';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
    if (!iso) return '-';
    try { return format(new Date(iso + 'T00:00:00'), 'dd/MM/yyyy', { locale: fr }); }
    catch { return iso; }
};

const firstOfMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ============================================================================
// Dialog : ajouter un déménageur au pointage d'une tâche
// ============================================================================
function PointageDialog({ open, onOpenChange, task, demenageursNames, onAdded }) {
    const [demenageurNom, setDemenageurNom] = useState('');
    const [nbJour, setNbJour] = useState('1');
    const [autres, setAutres] = useState('0');
    const [panier, setPanier] = useState(''); // 'oui' | 'non'
    const [retard, setRetard] = useState('');
    const [remarque, setRemarque] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            setDemenageurNom(''); setNbJour('1'); setAutres('0');
            setPanier(''); setRetard(''); setRemarque('');
        }
    }, [open]);

    const exists = demenageurNom && demenageursNames.some(
        n => n.toLowerCase() === demenageurNom.trim().toLowerCase(),
    );

    const submit = async () => {
        if (!task) return;
        if (!demenageurNom.trim()) return toast.error('Nom du déménageur obligatoire');
        if (!exists) return toast.error(`Le déménageur "${demenageurNom}" n'existe pas. Créez-le d'abord dans l'onglet "Déménageurs".`);
        if (nbJour === '' || Number(nbJour) < 0) return toast.error('Nombre de jour obligatoire');
        if (autres === '' || Number(autres) < 0) return toast.error('Autres obligatoire');
        if (!panier) return toast.error('Sélectionnez Panier (Oui/Non)');
        if (!retard) return toast.error('Sélectionnez Retard (Oui/Non)');

        setSubmitting(true);
        try {
            await axios.post(`${API}/api/exploitation/pointage/entry`, {
                source_id: task.source_id,
                client_id: task.client_id || '',
                client_nom: task.client_nom,
                date: task.date,
                line: {
                    demenageur_nom: demenageurNom.trim(),
                    nb_jour: Number(nbJour),
                    autres: Number(autres),
                    panier: panier === 'oui',
                    retard: retard === 'oui',
                    remarque: remarque.trim(),
                },
            }, { withCredentials: true });
            toast.success(`${demenageurNom} pointé`);
            onAdded();
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors du pointage');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="pointage-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ClipboardCheck size={18} className="text-[#2e7cd6]" />
                        Ajouter un déménageur au pointage
                    </DialogTitle>
                </DialogHeader>
                {task && (
                    <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 mb-2">
                        <span className="font-semibold text-slate-700">{task.client_nom}</span> — {formatDate(task.date)}
                    </div>
                )}
                <div className="space-y-3">
                    <div>
                        <Label className="text-sm">Nom du déménageur *</Label>
                        <Input
                            list="demenageurs-datalist"
                            value={demenageurNom}
                            onChange={(e) => setDemenageurNom(e.target.value)}
                            placeholder="Taper ou choisir…"
                            data-testid="pt-dem-nom"
                        />
                        <datalist id="demenageurs-datalist">
                            {demenageursNames.map(n => <option key={n} value={n} />)}
                        </datalist>
                        {demenageurNom && !exists && (
                            <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} /> Ce déménageur n'existe pas. Créez-le dans l'onglet "Déménageurs".
                            </p>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-sm">Nombre de jour *</Label>
                            <Input type="number" step="0.5" min="0" value={nbJour} onChange={e => setNbJour(e.target.value)} data-testid="pt-nbjour" />
                        </div>
                        <div>
                            <Label className="text-sm">Autres *</Label>
                            <Input type="number" step="0.5" min="0" value={autres} onChange={e => setAutres(e.target.value)} data-testid="pt-autres" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-sm">Panier *</Label>
                            <Select value={panier} onValueChange={setPanier}>
                                <SelectTrigger data-testid="pt-panier"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="oui">Oui</SelectItem>
                                    <SelectItem value="non">Non</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-sm">Retard *</Label>
                            <Select value={retard} onValueChange={setRetard}>
                                <SelectTrigger data-testid="pt-retard"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="oui">Oui</SelectItem>
                                    <SelectItem value="non">Non</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-sm">Remarque</Label>
                        <Textarea rows={2} value={remarque} onChange={e => setRemarque(e.target.value)} placeholder="(facultatif)" data-testid="pt-remarque" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Annuler</Button>
                    <Button onClick={submit} disabled={submitting} className="bg-[#2e7cd6] hover:bg-[#2568b5] text-white" data-testid="pt-submit">
                        {submitting ? <><Loader2 size={14} className="mr-1 animate-spin" /> Envoi…</> : 'Ajouter'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ============================================================================
// Dialog : Nouveau / Editer déménageur
// ============================================================================
function DemenageurFormDialog({ open, onOpenChange, initial, onSaved }) {
    const [nom, setNom] = useState('');
    const [telephone, setTelephone] = useState('');
    const [email, setEmail] = useState('');
    const [tarif, setTarif] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setNom(initial?.nom || '');
            setTelephone(initial?.telephone || '');
            setEmail(initial?.email || '');
            setTarif(initial ? String(initial.tarif_jour) : '');
        }
    }, [open, initial]);

    const submit = async () => {
        if (!nom.trim()) return toast.error('Nom obligatoire');
        if (!telephone.trim()) return toast.error('Téléphone obligatoire');
        if (tarif === '' || Number(tarif) < 0) return toast.error('Tarif/jour invalide');

        setSubmitting(true);
        const body = { nom: nom.trim(), telephone: telephone.trim(), email: email.trim(), tarif_jour: Number(tarif) };
        try {
            if (initial) {
                await axios.put(`${API}/api/exploitation/demenageurs/${initial.id}`, body, { withCredentials: true });
                toast.success('Déménageur mis à jour');
            } else {
                await axios.post(`${API}/api/exploitation/demenageurs`, body, { withCredentials: true });
                toast.success('Déménageur créé');
            }
            onSaved();
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="dem-form-dialog">
                <DialogHeader>
                    <DialogTitle>{initial ? 'Modifier le déménageur' : 'Nouveau déménageur'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-sm">Nom *</Label>
                        <Input value={nom} onChange={e => setNom(e.target.value)} data-testid="dem-nom" />
                    </div>
                    <div>
                        <Label className="text-sm">Téléphone *</Label>
                        <Input value={telephone} onChange={e => setTelephone(e.target.value)} data-testid="dem-tel" />
                    </div>
                    <div>
                        <Label className="text-sm">Email (optionnel)</Label>
                        <Input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="dem-email" />
                    </div>
                    <div>
                        <Label className="text-sm">Tarif / jour (€) *</Label>
                        <Input type="number" step="0.01" min="0" value={tarif} onChange={e => setTarif(e.target.value)} data-testid="dem-tarif" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Annuler</Button>
                    <Button onClick={submit} disabled={submitting} className="bg-[#2e7cd6] hover:bg-[#2568b5] text-white" data-testid="dem-save">
                        {submitting ? <><Loader2 size={14} className="mr-1 animate-spin" /> …</> : (initial ? 'Enregistrer' : 'Créer')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ============================================================================
// Page principale
// ============================================================================
export default function PointagePage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { can } = usePermissions();
    const canSeeDemenageursTab = can('exploitation.pointage_demenageurs.tab_demenageurs', 'read');
    const initialTab = searchParams.get('tab') === 'demenageurs' && canSeeDemenageursTab ? 'demenageurs' : 'pointage';
    const [tab, setTab] = useState(initialTab);

    // Pointage data
    const [ptFromDate, setPtFromDate] = useState(firstOfMonth());
    const [ptToDate, setPtToDate] = useState(todayISO());
    // Recherche par prestation : nom client / type prestation / commercial
    const [ptSearch, setPtSearch] = useState('');
    const [tasks, setTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(true);

    // Déménageurs data
    const [demFromDate, setDemFromDate] = useState(firstOfMonth());
    const [demToDate, setDemToDate] = useState(todayISO());
    const [demenageurs, setDemenageurs] = useState([]);
    const [demLoading, setDemLoading] = useState(true);

    // Dialogs
    const [pointageOpen, setPointageOpen] = useState(false);
    const [activeTask, setActiveTask] = useState(null);
    const [demFormOpen, setDemFormOpen] = useState(false);
    // Quand l'utilisateur ouvre le PointageDialog depuis la modale "Déménageurs pointés",
    // on revient automatiquement sur cette modale après l'ajout pour permettre
    // d'enchaîner plusieurs ajouts sans rechercher la prestation à nouveau.
    const [returnToPtList, setReturnToPtList] = useState(false);

    // Dialog liste des déménageurs pointés pour une prestation
    const [ptListOpen, setPtListOpen] = useState(false);
    const [ptListTask, setPtListTask] = useState(null);

    // Pagination du tableau Pointage : 15 lignes par page, plus récent en haut.
    const PAGE_SIZE_PT = 15;
    const [ptPage, setPtPage] = useState(1);

    const fetchTasks = useCallback(async () => {
        setTasksLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/api/exploitation/pointage/tasks?from_date=${ptFromDate}&to_date=${ptToDate}`,
                { withCredentials: true },
            );
            setTasks(data?.tasks || []);
        } catch { setTasks([]); }
        finally { setTasksLoading(false); }
    }, [ptFromDate, ptToDate]);

    const fetchDemenageurs = useCallback(async () => {
        setDemLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/api/exploitation/demenageurs?from_date=${demFromDate}&to_date=${demToDate}`,
                { withCredentials: true },
            );
            setDemenageurs(data.demenageurs || []);
        } catch { setDemenageurs([]); }
        finally { setDemLoading(false); }
    }, [demFromDate, demToDate]);

    useEffect(() => { fetchTasks(); }, [fetchTasks]);
    useEffect(() => { fetchDemenageurs(); }, [fetchDemenageurs]);

    // Reset à la page 1 quand on change de plage de dates ou de tab.
    useEffect(() => { setPtPage(1); }, [ptFromDate, ptToDate, tab, ptSearch]);

    // Filtre côté client : recherche par nom client, type prestation, commercial
    const filteredTasks = useMemo(() => {
        const q = ptSearch.trim().toLowerCase();
        if (!q) return tasks;
        return tasks.filter(t => {
            return (
                (t.client_nom || '').toLowerCase().includes(q) ||
                (t.type_prestation || '').toLowerCase().includes(q) ||
                (t.commercial_nom || '').toLowerCase().includes(q) ||
                (t.date || '').includes(q)
            );
        });
    }, [tasks, ptSearch]);

    const ptTotalPages = Math.max(1, Math.ceil(filteredTasks.length / PAGE_SIZE_PT));
    const ptPageSafe = Math.min(ptPage, ptTotalPages);
    const ptPagedTasks = filteredTasks.slice((ptPageSafe - 1) * PAGE_SIZE_PT, ptPageSafe * PAGE_SIZE_PT);

    const demenageursNames = useMemo(() => demenageurs.map(d => d.nom), [demenageurs]);

    const openPointage = (task) => {
        setActiveTask(task);
        setPointageOpen(true);
    };

    const removeLine = async (entryId, demId, demNom) => {
        if (!window.confirm(`Retirer ${demNom} de ce pointage ?`)) return;
        try {
            await axios.delete(`${API}/api/exploitation/pointage/entry/${entryId}/line/${demId}`, { withCredentials: true });
            toast.success('Déménageur retiré');
            fetchTasks();
            fetchDemenageurs();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const deleteDem = async (id, nom) => {
        if (!window.confirm(`Supprimer ${nom} ? Il sera placé dans la corbeille (récupérable 30 jours).`)) return;
        try {
            await axios.delete(`${API}/api/exploitation/demenageurs/${id}`, { withCredentials: true });
            toast.success('Déménageur supprimé');
            fetchDemenageurs();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="pointage-page">
            <div className="flex items-center gap-3 mb-6">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/exploitation')}
                    className="text-slate-700 border-slate-300 hover:bg-slate-100"
                    data-testid="back-btn"
                >
                    <ArrowLeft size={16} className="mr-1" /> Retour
                </Button>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Pointage déménageurs
                    </h1>
                    <p className="mt-1 text-slate-500 text-sm">
                        Suivi des heures, paniers et retards par déménageur.
                    </p>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="mb-4">
                    <TabsTrigger
                        value="pointage"
                        className="data-[state=active]:bg-blue-100 data-[state=active]:text-[#2e7cd6] data-[state=active]:shadow-sm data-[state=active]:font-semibold"
                        data-testid="tab-pointage"
                    >
                        <ClipboardCheck size={14} className="mr-1.5" /> Pointage
                    </TabsTrigger>
                    {canSeeDemenageursTab && (
                    <TabsTrigger
                        value="demenageurs"
                        className="data-[state=active]:bg-blue-100 data-[state=active]:text-[#2e7cd6] data-[state=active]:shadow-sm data-[state=active]:font-semibold"
                        data-testid="tab-demenageurs"
                    >
                        <Users size={14} className="mr-1.5" /> Déménageurs ({demenageurs.length})
                    </TabsTrigger>
                    )}
                </TabsList>

                {/* ---------- Onglet Pointage ---------- */}
                <TabsContent value="pointage">
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-4">
                        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
                            <div>
                                <Label className="text-xs text-slate-600">Du</Label>
                                <Input type="date" value={ptFromDate} onChange={e => setPtFromDate(e.target.value)} className="w-40" data-testid="pt-from" />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-600">Au</Label>
                                <Input type="date" value={ptToDate} onChange={e => setPtToDate(e.target.value)} className="w-40" data-testid="pt-to" />
                            </div>
                            <div className="flex-1 min-w-[220px]">
                                <Label className="text-xs text-slate-600">Rechercher une prestation</Label>
                                <div className="relative">
                                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <Input
                                        value={ptSearch}
                                        onChange={e => setPtSearch(e.target.value)}
                                        placeholder="Nom client, type, commercial..."
                                        className="pl-9"
                                        data-testid="pt-search"
                                    />
                                    {ptSearch && (
                                        <button
                                            type="button"
                                            onClick={() => setPtSearch('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400"
                                            title="Effacer"
                                            data-testid="pt-search-clear"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="text-xs text-slate-500 ml-auto self-center">
                                {ptSearch
                                    ? `${filteredTasks.length} sur ${tasks.length} prestation${tasks.length > 1 ? 's' : ''}`
                                    : `${tasks.length} prestation${tasks.length > 1 ? 's' : ''} sur la période`}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm">
                        <CardContent className="p-0">
                            {tasksLoading ? (
                                <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
                            ) : filteredTasks.length === 0 ? (
                                <div className="p-10 text-center text-slate-500 text-sm">
                                    {ptSearch
                                        ? <>Aucune prestation ne correspond à <span className="font-medium text-slate-700">"{ptSearch}"</span></>
                                        : "Aucune prestation à pointer pour l'instant."}
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-slate-50/70">
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Client</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Date</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Type</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Ville</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Déménageurs pointés</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-right whitespace-nowrap">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {ptPagedTasks.map((t) => (
                                            <TableRow
                                                key={t.source_id}
                                                className={`transition-colors ${t.status === 'pending' ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'}`}
                                                data-testid={`pt-row-${t.source_id}`}
                                            >
                                                <TableCell className="px-5 font-medium text-slate-900 whitespace-nowrap">
                                                    <span className="inline-flex items-center gap-2">
                                                        {t.event_type === 'livraison'
                                                            ? (
                                                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-50 text-amber-500" title="Livraison">
                                                                    <Package size={14} />
                                                                </span>
                                                            )
                                                            : (
                                                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 text-[#2e7cd6]" title="Déménagement">
                                                                    <Truck size={14} />
                                                                </span>
                                                            )}
                                                        {t.client_nom || '-'}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-5 whitespace-nowrap text-slate-600 text-xs">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Calendar size={13} className="text-slate-400" />
                                                        {formatDate(t.date)}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-5 whitespace-nowrap">
                                                    <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">{t.type_prestation || '-'}</Badge>
                                                </TableCell>
                                                <TableCell className="px-5 whitespace-nowrap text-slate-600 text-xs">
                                                    {(() => {
                                                        const type = (t.type_prestation || '').toLowerCase();
                                                        const isDechargement = type.includes('déchargement') || type.includes('dechargement') || type === 'livraison';
                                                        const ville = isDechargement ? t.ville_arrivee : t.ville_depart;
                                                        return ville
                                                            ? <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-slate-400" />{ville}</span>
                                                            : <span className="text-slate-400">-</span>;
                                                    })()}
                                                </TableCell>
                                                <TableCell className="px-5">
                                                    {t.pointages.length === 0
                                                        ? <span className="text-slate-400 text-xs italic">Aucun pointage</span>
                                                        : (
                                                            <button
                                                                type="button"
                                                                onClick={() => { setPtListTask(t); setPtListOpen(true); }}
                                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium transition-all"
                                                                data-testid={`pt-count-${t.source_id}`}
                                                            >
                                                                <CheckCircle2 size={12} />
                                                                {t.pointages.length} déménageur{t.pointages.length > 1 ? 's' : ''}
                                                            </button>
                                                        )}
                                                </TableCell>
                                                <TableCell className="px-5 text-right whitespace-nowrap">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => openPointage(t)}
                                                        className="h-8 text-xs bg-[#2e7cd6] hover:bg-[#2568b5] text-white"
                                                        data-testid={`pt-btn-${t.source_id}`}
                                                    >
                                                        <Plus size={13} className="mr-1" />
                                                        Pointage
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                            {/* Pagination 15 par page (les plus récents en haut, anciens en bas) */}
                            {filteredTasks.length > PAGE_SIZE_PT && (
                                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-200 bg-slate-50/50">
                                    <p className="text-xs text-slate-500">
                                        {(ptPageSafe - 1) * PAGE_SIZE_PT + 1}–{Math.min(ptPageSafe * PAGE_SIZE_PT, filteredTasks.length)} sur {filteredTasks.length}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8"
                                            disabled={ptPageSafe <= 1}
                                            onClick={() => setPtPage(p => Math.max(1, p - 1))}
                                            data-testid="pt-prev-page"
                                        >
                                            ← Précédent
                                        </Button>
                                        <span className="text-xs text-slate-600 px-2">
                                            Page <strong>{ptPageSafe}</strong> / {ptTotalPages}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8"
                                            disabled={ptPageSafe >= ptTotalPages}
                                            onClick={() => setPtPage(p => Math.min(ptTotalPages, p + 1))}
                                            data-testid="pt-next-page"
                                        >
                                            Suivant →
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ---------- Onglet Déménageurs ---------- */}
                {canSeeDemenageursTab && (
                <TabsContent value="demenageurs">
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-4">
                        <CardContent className="p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
                            <div>
                                <Label className="text-xs text-slate-600">Du</Label>
                                <Input type="date" value={demFromDate} onChange={e => setDemFromDate(e.target.value)} className="w-40" data-testid="dem-from" />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-600">Au</Label>
                                <Input type="date" value={demToDate} onChange={e => setDemToDate(e.target.value)} className="w-40" data-testid="dem-to" />
                            </div>
                            <div className="flex-1" />
                            <Button
                                onClick={() => setDemFormOpen(true)}
                                className="bg-[#2e7cd6] hover:bg-[#2568b5] text-white"
                                data-testid="new-dem-btn"
                            >
                                <Plus size={14} className="mr-1" /> Nouveau déménageur
                            </Button>
                        </CardContent>
                    </Card>

                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm">
                        <CardContent className="p-0">
                            {demLoading ? (
                                <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
                            ) : demenageurs.length === 0 ? (
                                <div className="p-10 text-center text-slate-500 text-sm">Aucun déménageur.</div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-slate-50/70">
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold">Nom</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-right">Total jour</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-right">Total autres</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-right">Panier</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-right">Retard</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-center">Calcul dernier mois</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-right">Solde total</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {[...demenageurs].sort((a, b) => (b.total_jour || 0) - (a.total_jour || 0)).map((d) => (
                                            <TableRow key={d.id} className="hover:bg-slate-50" data-testid={`dem-row-${d.id}`}>
                                                <TableCell className="px-5 font-medium text-slate-900 whitespace-nowrap">
                                                    <span className="inline-flex items-center gap-2">
                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-purple-50 text-purple-600" title="Déménageur">
                                                            <User size={14} />
                                                        </span>
                                                        {d.nom}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-5 text-right font-medium">{d.total_jour}</TableCell>
                                                <TableCell className="px-5 text-right">{d.total_autres}</TableCell>
                                                <TableCell className="px-5 text-right">{d.total_panier}</TableCell>
                                                <TableCell className="px-5 text-right">{d.total_retard}</TableCell>
                                                <TableCell className="px-5 text-center">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${d.calcul_dernier_mois ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                                                        data-testid={`dem-calc-dernier-mois-${d.id}`}
                                                    >
                                                        {d.calcul_dernier_mois ? 'Oui' : 'Non'}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-5 text-right whitespace-nowrap">
                                                    <span
                                                        className={`font-semibold ${(d.solde_total || 0) < 0 ? 'text-blue-600' : (d.solde_total || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}
                                                        data-testid={`dem-solde-total-${d.id}`}
                                                    >
                                                        {(d.solde_total || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-5 text-right whitespace-nowrap">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => navigate(`/exploitation/pointage-demenageurs/demenageur/${d.id}`)}
                                                            className="h-8 text-xs"
                                                            data-testid={`dem-detail-${d.id}`}
                                                        >
                                                            <Eye size={13} className="mr-1" /> Aperçu
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => deleteDem(d.id, d.nom)}
                                                            className="h-8 w-8 p-0 text-red-500"
                                                            data-testid={`dem-delete-${d.id}`}
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow className="bg-slate-50/70 border-t-2 border-slate-200">
                                            <TableCell colSpan={6} className="px-5 font-bold text-slate-900 text-right uppercase text-xs">Total</TableCell>
                                            <TableCell className="px-5 text-right whitespace-nowrap">
                                                {(() => {
                                                    const t = demenageurs.reduce((s, d) => s + (Number(d.solde_total) || 0), 0);
                                                    return (
                                                        <span
                                                            className={`font-bold ${t < 0 ? 'text-blue-600' : t > 0 ? 'text-red-600' : 'text-emerald-600'}`}
                                                            data-testid="dem-solde-total-general"
                                                        >
                                                            {t.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                                        </span>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell className="px-5" />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
                )}
            </Tabs>

            <PointageDialog
                open={pointageOpen}
                onOpenChange={(open) => {
                    setPointageOpen(open);
                    // Si on ferme le dialog sans avoir ajouté (clic sur la croix / Echap),
                    // mais qu'on est venu depuis la liste "Déménageurs pointés",
                    // on rouvre quand même la liste pour ne pas perdre le contexte.
                    if (!open && returnToPtList) {
                        setReturnToPtList(false);
                        setPtListOpen(true);
                    }
                }}
                task={activeTask}
                demenageursNames={demenageursNames}
                onAdded={async () => {
                    await fetchTasks();
                    fetchDemenageurs();
                    // Après ajout réussi, si on était venu depuis la liste, on y retourne
                    // avec les données fraîchement rechargées.
                    if (returnToPtList) {
                        setReturnToPtList(false);
                        // attend le prochain tick pour que la liste tasks soit à jour
                        setTimeout(() => {
                            setTasks(currentTasks => {
                                if (activeTask) {
                                    const refreshed = currentTasks.find(
                                        t => t.entry_id === activeTask.entry_id || (t.client_id === activeTask.client_id && t.date === activeTask.date && t.type_prestation === activeTask.type_prestation),
                                    );
                                    if (refreshed) setPtListTask(refreshed);
                                }
                                return currentTasks;
                            });
                            setPtListOpen(true);
                        }, 0);
                    }
                }}
            />
            <DemenageurFormDialog
                open={demFormOpen}
                onOpenChange={setDemFormOpen}
                onSaved={fetchDemenageurs}
            />

            {/* Dialog : liste des déménageurs pointés pour une prestation */}
            <Dialog open={ptListOpen} onOpenChange={setPtListOpen}>
                <DialogContent className="max-w-lg" data-testid="pt-list-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Users size={18} className="text-[#2e7cd6]" />
                            Déménageurs pointés
                            {ptListTask && (
                                <span className="text-slate-500 font-normal text-sm">
                                    — {ptListTask.client_nom} ({formatDate(ptListTask.date)})
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    {ptListTask && (
                        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                            {ptListTask.pointages.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-4">Aucun déménageur pointé.</p>
                            ) : ptListTask.pointages.map((p, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-center gap-3 p-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
                                    data-testid={`pt-list-line-${idx}`}
                                >
                                    <div className="w-9 h-9 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                                        <User size={16} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-slate-900">{p.demenageur_nom}</p>
                                        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-600">
                                            <span><span className="text-slate-500">Jour</span> <strong>{p.nb_jour}</strong></span>
                                            {p.autres > 0 && <span><span className="text-slate-500">Autres</span> <strong>{p.autres}</strong></span>}
                                            {p.panier && <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">🍱 Panier</Badge>}
                                            {p.retard && <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">⏱ Retard</Badge>}
                                        </div>
                                        {p.remarque && (
                                            <p className="text-xs text-slate-500 mt-1 italic">"{p.remarque}"</p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            await removeLine(ptListTask.entry_id, p.demenageur_id, p.demenageur_nom);
                                            setPtListTask(prev => prev ? {
                                                ...prev,
                                                pointages: prev.pointages.filter((_, i) => i !== idx),
                                            } : prev);
                                        }}
                                        className="p-2 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 shrink-0"
                                        title={`Retirer ${p.demenageur_nom}`}
                                        data-testid={`pt-list-remove-${idx}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            onClick={() => {
                                setPtListOpen(false);
                                setActiveTask(ptListTask);
                                setReturnToPtList(true);
                                setPointageOpen(true);
                            }}
                            className="bg-[#2e7cd6] hover:bg-[#2568b5] text-white"
                            data-testid="pt-list-add-btn"
                        >
                            <Plus size={14} className="mr-1" /> Ajouter un déménageur
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
